import { HL } from "../kernel.js";

/**
 * Mark: Amoreg's R, a slab standing up, turned a little off facing. Its diagonal
 * runs from the top corner through the bowl, and comes out past it as the
 * leg. At rest the leg stands a little out along that line, bright; as the
 * pointer comes near the mark it slides home on one spring, to the logo's own
 * gap, and far off on the stage it stands further out. The slider is how far
 * out it stands at the far end, in the mark's units (its stroke is 13.5).
 *
 * The pattern: padlock's. One spring on the leg's offset, set by the
 * pointer's distance to the body, which never moves. The glyph is not
 * convex, so each slab's outline is worked out once: the back outline is kept
 * only where nothing of the slab stands in front of it.
 */
const { Cam, clamp, fillet, fit, open, poly, proj, rad, r2, spring, stepS, disposer, mk, pointer, register } = HL;

// The mark in its own plane, u right and v down: one stroke, a bowl about (CX, CY), the diagonal at A.
const SW = 13.5, CX = 31.5, CY = 30.25, RO = 30.25, GAP = 10.4, BAR = 47, BASE = 74, A = rad(47.3);
// The slab: depth, crease inset, corner radius, turn; the reach: R0 lands it, R1 is the far end; REST of it at rest.
const DEPTH = 7, B = 1.2, FR = 1.4, TURN = 56, S = 2.6, R0 = 100, R1 = 250, REST = 0.35, MAX = 32;
const DIR = [Math.cos(A), Math.sin(A)], NRM = [Math.sin(A), -Math.cos(A)];
const BACK = [-DIR[0], -DIR[1]];
const RED = "var(--hairline-accent, #de5656)";
const LEG_INK = `--hl-edge:${RED};--hl-hi:${RED};--hl-lo:color-mix(in oklab, ${RED} 45%, var(--hl-plate))`;

/** A line through p walked along d (s picks its root on a circle), and the bowl's circle walked clockwise (w 1) or back (w -1). */
const L = (p, d, s = 1) => ({ p, d, s });
const O = (r, w) => ({ r, w });
/** A point on the diagonal's line, moved k toward the bowl: k 0 is its lower edge, SW its upper. */
const diag = (k) => [NRM[0] * k, SW + NRM[1] * k];

/** The glyph's outlines with every edge moved b into the solid: the body (outside, then the counter) and the leg. */
const glyph = (b) => ({
  body: [
    [L([0, b], [1, 0]), O(RO - b, 1), L([0, 2 * CY - b], [-1, 0]), L([SW - b, 0], [0, 1]), L([0, BASE - b], [-1, 0]),
      L([b, 0], [0, -1]), L([0, BAR + b], [1, 0]), L(diag(b), BACK), L([b, 0], [0, -1])],
    [L([0, SW - b], [-1, 0]), L(diag(SW - b), DIR, 1), O(RO - SW + b, -1)],
  ].map(outline),
  leg: [[O(RO + GAP + b, -1), L(diag(SW - b), DIR, 1), L([0, BASE - b], [-1, 0]), L(diag(b), BACK, -1)]].map(outline),
});

/** Where one edge hands over to the next; the third value says the two meet tangent, with no corner to round. */
function meet(a, c) {
  if (a.d && c.d) {
    const t = ((c.p[0] - a.p[0]) * c.d[1] - (c.p[1] - a.p[1]) * c.d[0]) / (a.d[0] * c.d[1] - a.d[1] * c.d[0]);
    return [a.p[0] + t * a.d[0], a.p[1] + t * a.d[1], false];
  }
  const l = a.d ? a : c, r = (a.d ? c : a).r, t = (CX - l.p[0]) * l.d[0] + (CY - l.p[1]) * l.d[1];
  const f = [l.p[0] + t * l.d[0], l.p[1] + t * l.d[1]];
  const h = Math.sqrt(Math.max(0, r * r - (CX - f[0]) ** 2 - (CY - f[1]) ** 2));
  return [f[0] + l.s * h * l.d[0], f[1] + l.s * h * l.d[1], h < 0.5];
}

/** A closed outline from its edges: corners rounded a little, the mark's own way; arcs sampled every 1.5 units. */
function outline(edges) {
  const n = edges.length, V = edges.map((e, i) => meet(e, edges[(i + 1) % n])), pts = [], radii = [];
  edges.forEach((e, i) => {
    if (e.r) {
      const s = V[(i + n - 1) % n], f = V[i], a0 = Math.atan2(s[1] - CY, s[0] - CX);
      let a1 = Math.atan2(f[1] - CY, f[0] - CX);
      while (e.w * (a1 - a0) <= 0) a1 += e.w * 2 * Math.PI;
      const k = Math.ceil((Math.abs(a1 - a0) * e.r) / 1.5);
      for (let j = 1; j < k; j++) {
        const a = a0 + ((a1 - a0) * j) / k;
        pts.push([CX + e.r * Math.cos(a), CY + e.r * Math.sin(a)]); radii.push(0);
      }
    }
    pts.push([V[i][0], V[i][1]]); radii.push(V[i][2] ? 0 : FR);
  });
  return fillet(pts, radii, 4).filter((q, i, a) => {
    const p = a[(i + a.length - 1) % a.length];
    return Math.hypot(q[0] - p[0], q[1] - p[1]) > 1e-6;
  });
}

const mid = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

/**
 * One slab into g, from its front outlines F and crease outlines K on screen, and sv, the screen step from its back
 * face to its front. Painted back to front: the fills, then the outline, then the crease. Returns the outline's path.
 */
function slab(g, F, K, sv) {
  const bk = (p) => [p[0] - sv[0], p[1] - sv[1]];
  // An edge whose outward normal points against sv has a wall behind it that shows.
  const up = (a, b) => (b[1] - a[1]) * sv[0] - (b[0] - a[0]) * sv[1] < 0;
  const inside = (q) => {
    let c = false;
    for (const l of F) for (let i = 0, j = l.length - 1; i < l.length; j = i++) {
      const a = l[i], b = l[j];
      if (a[1] > q[1] !== b[1] > q[1] && q[0] < ((b[0] - a[0]) * (q[1] - a[1])) / (b[1] - a[1]) + a[0]) c = !c;
    }
    return c;
  };
  // A point is hidden when a step towards the front from it lands inside the front face; tested a hair outside the
  // line it is on, so a point on the face's own edge never counts.
  const hidden = (q, n) => {
    for (let k = 0; k < 12; k++) if (inside([q[0] + 0.05 * n[0] + ((k + 0.5) / 12) * sv[0], q[1] + 0.05 * n[1] + ((k + 0.5) / 12) * sv[1]])) return true;
    return false;
  };
  const nrm = (a, b) => { const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1; return [(b[1] - a[1]) / l, (a[0] - b[0]) / l]; };
  const runs = [], quads = [];
  let cur = null;
  const add = (a, b, n) => {
    const test = !!n;
    const m = test ? Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]))) : 1;
    for (let k = 0; k < m; k++) {
      if (test && hidden(mid(a, b, (k + 0.5) / m), n)) { cur = null; continue; }
      if (!cur) runs.push((cur = [mid(a, b, k / m)]));
      cur.push(mid(a, b, (k + 1) / m));
    }
  };
  for (const l of F) {
    // Seen through a hole, the opening is where its front and back cross, so a hole's corners sweep nothing into view.
    const hole = l.reduce((t, a, i) => t + a[0] * l[(i + 1) % l.length][1] - l[(i + 1) % l.length][0] * a[1], 0) < 0;
    cur = null;
    l.forEach((a, i) => {
      const b = l[(i + 1) % l.length], c = l[(i + 2) % l.length], s0 = up(a, b), s1 = up(b, c);
      const n0 = nrm(a, b), n1 = nrm(b, c);
      if (s0) { add(bk(a), bk(b), n0); quads.push(poly([bk(a), bk(b), b, a])); } else add(a, b, null);
      if (s0 !== s1 && !hole) add(s0 ? bk(b) : b, s0 ? b : bk(b), [n0[0] + n1[0], n0[1] + n1[1]]);
    });
  }
  const crease = [];
  for (const l of K) {
    const n = l.length, st = l.findIndex((a, i) => !up(a, l[(i + 1) % n]));
    let c = null;
    for (let k = 0; k < n; k++) {
      const a = l[(st + k) % n], b = l[(st + k + 1) % n];
      if (up(a, b)) { if (!c) crease.push((c = [a])); c.push(b); } else c = null;
    }
  }
  mk("path", { d: F.map((l) => poly(l.map(bk))).join(""), class: "fo" }, g);
  mk("path", { d: quads.join(""), class: "fo" }, g);
  mk("path", { d: F.map(poly).join(""), class: "fo" }, g);
  mk("path", { d: crease.map(open).join(""), class: "nf lo" }, g);
  return mk("path", { d: runs.map(open).join(""), class: "nf sil" }, g);
}

function mount({ stage, svg, read }, value) {
  const bag = disposer();
  let far = value, over = false, o = REST;

  // The mark stands on its baseline, its plane turned TURN degrees; fitted with the leg at its furthest.
  const cs = Math.cos(rad(TURN)), sn = Math.sin(rad(TURN));
  const w3 = (u, v, d) => [u * cs + d * sn, -u * sn + d * cs, BASE - v];
  const C = Cam(45, 0.5, S), ext = [];
  const [mu, mv] = [MAX * DIR[0], MAX * DIR[1]];
  for (const [u, v] of [[0, 0], [73, 0], [0, BASE], [73 + mu, BASE + mv]]) for (const d of [0, DEPTH]) ext.push(w3(u, v, d));
  fit(C, ext, 200, 166);
  const P = proj(C), Q = (u, v, d) => P(...w3(u, v, d));
  const o0 = Q(0, 0, 0), f0 = Q(0, 0, DEPTH), sv = [f0[0] - o0[0], f0[1] - o0[1]];
  const scr = (loops) => loops.map((l) => l.map(([u, v]) => Q(u, v, DEPTH)));
  const G0 = glyph(0), GB = glyph(B);

  const g = mk("g", {}, svg);
  const body = slab(mk("g", {}, g), scr(G0.body), scr(GB.body), sv);
  // The brand keeps the leg in Signal Red: the symbol is never drawn entirely in ink.
  const legG = mk("g", { style: LEG_INK }, g), leg = slab(legG, scr(G0.leg), scr(GB.leg), sv);

  // The leg arrives from its far end and settles at rest once, when the mark first draws.
  const sp = spring(MAX);
  let drawn = NaN;
  function draw() {
    if (sp.x === drawn) return;
    drawn = sp.x;
    const q = Q(sp.x * DIR[0], sp.x * DIR[1], 0);
    legG.setAttribute("transform", `translate(${r2(q[0] - o0[0])} ${r2(q[1] - o0[1])})`);
  }
  const loop = register(stage, (dt) => { const m = stepS(sp, dt); draw(); return m; });
  bag.add(loop.unregister);

  // Read from the target, never the pose on screen; the body it is measured to never moves.
  function retarget() {
    sp.t = far * o;
    const landed = over && sp.t < 0.5;
    read.textContent = !over ? "rest" : landed ? "landed" : "gap " + Math.round(sp.t);
    leg.classList.toggle("hi", !landed);
    body.classList.toggle("hi", landed);
    loop.wake();
  }
  retarget();

  const c0 = Q(36.5, 37, DEPTH / 2);
  bag.add(pointer(stage, {
    move: (p) => { over = true; o = clamp((Math.hypot(p[0] - c0[0], p[1] - c0[1]) - R0) / (R1 - R0), 0, 1); retarget(); },
    leave: () => { over = false; o = REST; retarget(); },
  }));
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => { far = v; retarget(); },
    destroy: bag.dispose,
  };
}

export default {
  name: "mark",
  means: "Amoreg's R: its diagonal runs through the bowl and out as the leg, which slides home along that line as the pointer nears.",
  rules: [1, 3, 5, 9],
  range: [12, 22, 32],
  tour: [[390, 310], [270, 222], [180, 160], null],
  mount,
};
